import { Notification } from '@wellesley/openapi'
import { FC } from 'react'
import { UserAvatar } from '@wellesley/user'
import { selectSmallestPreview } from '@wellesley/uploads'
import { useSelector } from 'react-redux'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import { AppsLink } from '@wellesley/router'
import UserInfoPopup from 'components/UserInfoPopup'
import { cn } from '@wellesley/ui'
import cutUserInfo from '../../helpers/cutUserInfo'
import NotificationTitle from '../NotificationTitle'
import { platformSettingsSlice } from '@wellesley/settings'
import { getPastTimestamp } from 'helpers/time/getPastTimestamp'
import { usePostApiV1NotificationsByIdMarkMutation } from '../../api'
import getNotificationIcon from '../../helpers/getNotificationIcon'
import ControlsWrapper from '../notificationControls/ControlsWrapper'
import checkNotificationType from '../../helpers/checkNotificationType'
import removeTags from 'helpers/removeTags'

type CmpProps = {
  notification: Notification
  isPopup?: boolean
}

const NotificationBody: FC<CmpProps> = ({ notification, isPopup = false }) => {
  const extensions = useSelector(
    platformSettingsSlice.selectors.selectSupportedExtensions,
  )

  const imgExtension = extensions['IMAGE']

  const author = notification.fromUser
  const authorAvatar = author?.avatar
    ? selectSmallestPreview({
        upload: author.avatar,
        imgExtension,
      })
    : null

  const username = author ? getUserNameWithDomain(author) : ''

  const { isQuote, isReply, isLike, isFollowRequest, isUnread } =
    checkNotificationType(notification)

  const canShowOriginalPostPreview = isQuote || isReply

  const originalPostPreview =
    canShowOriginalPostPreview && notification.post?.originalPost
      ? removeTags(notification.post?.originalPost.text)
      : ''

  const mentionedFeed =
    isQuote || isLike
      ? notification.post
      : notification.post?.originalPost || notification.post

  const postFirstAttachment = mentionedFeed?.uploads[0]
  const postMediaPreview = postFirstAttachment
    ? selectSmallestPreview({
        upload: postFirstAttachment,
        imgExtension: imgExtension,
      })
    : null

  const isInternalUser = !author?.domain
  const hasAttachments = !!notification.post?.uploads.length

  const maxShortenedInfo =
    isFollowRequest || (hasAttachments && !isInternalUser)

  const userInfo = author
    ? cutUserInfo({
        user: author,
        maxShortenedInfo: maxShortenedInfo,
      })
    : { displayName: '', userName: '' }

  const hasPreview = postMediaPreview?.uri && mentionedFeed
  const isMediaHidden = !!(
    notification.post?.summary && notification.post.settings.sensitive
  )

  const [markNotificationAsRead] = usePostApiV1NotificationsByIdMarkMutation()

  const handleStatusClick = async () => {
    try {
      await markNotificationAsRead({
        id: notification.id,
        status: isUnread ? 'READ' : 'UNREAD',
      }).unwrap()
    } catch (error) {
      console.error('Mark notification error: ', error)
    }
  }

  return (
    <div className="p-xs rounded-m w-full text-sm hover:bg-gray-100">
      <div className="gap-s flex w-full min-w-0 items-start">
        {author && (
          <div className="relative shrink-0">
            <UserAvatar
              size={40}
              name={author.displayName || ''}
              imgUrl={authorAvatar?.uri || ''}
            />
            <img
              src={getNotificationIcon(notification)}
              className="size-xxs -right-xxs absolute bottom-0.5"
              alt={notification.type}
            />
          </div>
        )}
        <div className="gap-xs flex min-w-0 grow items-center">
          <div className="min-w-0 flex-1 overflow-hidden">
            {author && (
              <AppsLink
                to="/@/:username"
                props={{ username }}
                className="block min-w-0"
              >
                <UserInfoPopup canShow={!isPopup} username={username}>
                  <div
                    className="gap-xxxs mb-xxxs flex min-w-0 cursor-pointer items-center text-sm"
                    data-testid="notificationAuthor"
                  >
                    <div className="shrink-0 whitespace-nowrap font-semibold text-gray-900">
                      {userInfo.displayName}
                    </div>
                    <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-gray-500">
                      {userInfo.userName}
                    </div>
                  </div>
                </UserInfoPopup>
              </AppsLink>
            )}
            <div className="gap-xxxs mb-xxxs flex min-w-0 items-center">
              <div
                className="shrink-0 whitespace-nowrap text-sm font-semibold text-gray-900"
                data-testid="notificationTitle"
              >
                <NotificationTitle notification={notification} />
              </div>
              <div className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-gray-500">
                {originalPostPreview}
              </div>
            </div>
            {mentionedFeed && (
              <AppsLink
                to="/@/:username/posts/:postId"
                props={{
                  postId: mentionedFeed.id,
                  username: getUserNameWithDomain(mentionedFeed.author),
                }}
                className="mb-xxxs block min-w-0"
              >
                <div
                  className={cn(
                    'notificationBody mt-xxxs overflow-hidden text-ellipsis whitespace-nowrap text-gray-900',
                  )}
                  data-testid="mentionedFeed"
                  dangerouslySetInnerHTML={{ __html: mentionedFeed.text }}
                />
              </AppsLink>
            )}
            <div className="my-xxxs">
              {isPopup && (
                <ControlsWrapper notification={notification} isPopup={true} />
              )}
            </div>
            <div
              className="gap-xxxs inline-flex cursor-pointer items-center text-gray-500 outline-none"
              onClick={() => void handleStatusClick()}
            >
              <div
                data-testid="status-indicator"
                className={cn(
                  'size-2 rounded-full',
                  isUnread ? 'bg-blue-500' : 'border border-gray-300',
                )}
              />
              {getPastTimestamp(notification.createdAt)}
            </div>
          </div>
          <div className="relative shrink-0 empty:hidden">
            {hasPreview && (
              <AppsLink
                to="/@/:username/posts/:postId"
                props={{
                  postId: mentionedFeed.id,
                  username: getUserNameWithDomain(mentionedFeed.author),
                }}
              >
                {isMediaHidden && (
                  <div className="absolute z-[1] size-full backdrop-blur-[5px]" />
                )}
                <img
                  className={
                    'border-alpha-black-20 size-l block shrink-0 rounded-[6px] border'
                  }
                  src={postMediaPreview.uri!}
                  alt=""
                />
              </AppsLink>
            )}
            {!isPopup && (
              <ControlsWrapper notification={notification} isPopup={false} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default NotificationBody
